/**
 * QUIPWA — CardIndexService
 *
 * Tracks the note↔card links (`cards_index.json`) behind the "flashcards
 * vinculadas" list in the note editor.
 *
 * Local-first: the browser cache is the source the UI reads, and Drive is a
 * sync target. This index used to live only on Drive, so every read was a full
 * download that threw outright when the user was signed out or offline —
 * taking the note editor's flashcard section down with it.
 *
 * Cards are only ever added here, never edited in place, so two devices can be
 * reconciled by a union on id. That's what makes it safe to keep writing
 * locally when Drive is unreachable and push later.
 */

import { driveService } from '../drive/drive-service';
import { authSession } from '../auth/index.ts';
import { unionCards } from './cards-index-merge.ts';

/** Lightweight FSRS state for notes-derived quick cards — not the full review_state schema. */
export interface FlashcardFsrsState {
  status: string;
  [key: string]: unknown;
}

export interface Flashcard {
  id: string;
  front: string;
  back: string;
  source_note_id: string;
  /** ID of the Quipwa Notes-derived deck this card belongs to (stored in cards_index.json) */
  deck_id: string;
  fsrs_state?: FlashcardFsrsState;
  created_at: number;
}

export interface CardsIndexFile {
  version: 1;
  synced_at: number;
  cards: Flashcard[];
}

const CACHE_KEY = 'quipwa:cardsIndex';
const DIRTY_KEY = 'quipwa:cardsIndex.dirty';

function emptyIndex(): CardsIndexFile {
  return { version: 1, synced_at: 0, cards: [] };
}

function readCache(): CardsIndexFile | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CardsIndexFile;
    return parsed.version === 1 && Array.isArray(parsed.cards) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(index: CardsIndexFile, dirty: boolean): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(index));
    if (dirty) localStorage.setItem(DIRTY_KEY, '1');
    else localStorage.removeItem(DIRTY_KEY);
  } catch (err) {
    console.warn('[CardIndexService] Could not cache index:', err);
  }
}

function isDirty(): boolean {
  try {
    return localStorage.getItem(DIRTY_KEY) === '1';
  } catch {
    return false;
  }
}

export class CardIndexService {
  private indexFileId: string | null = null;
  private dataFolderId: string | null = null;
  private initialized = false;

  /**
   * Locate (or create) the Drive copy. Only meaningful when signed in; callers
   * must treat a rejection as "Drive unavailable", not as a fatal error.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    this.dataFolderId = await driveService.ensureDataFolder();
    const existing = await driveService.findFile('cards_index.json', this.dataFolderId);

    if (existing) {
      this.indexFileId = existing.id;
    } else {
      const seed = readCache() ?? emptyIndex();
      const file = await driveService.uploadJSON('cards_index.json', seed, this.dataFolderId);
      this.indexFileId = file.id;
    }
    this.initialized = true;
    console.info('[CardIndexService] Initialized:', this.indexFileId);
  }

  /**
   * The current index. Never throws: without Drive (signed out, offline, or a
   * failed request) it serves the local cache, which is what the UI needs to
   * keep rendering.
   */
  async getIndex(): Promise<CardsIndexFile> {
    const cached = readCache();

    if (authSession.state.status !== 'active') {
      return cached ?? emptyIndex();
    }

    try {
      await this.initialize();
      if (!this.indexFileId) return cached ?? emptyIndex();

      const remote = await driveService.downloadJSON<CardsIndexFile>(this.indexFileId);
      if (!cached) {
        writeCache(remote, false);
        return remote;
      }

      const merged = unionCards(cached, remote);
      // Anything the cache had that Drive doesn't is either an unpushed local
      // write or a card added on this device while offline — push it back
      // rather than let the next write silently drop it.
      const needsPush = isDirty() || merged.cards.length > remote.cards.length;
      writeCache(merged, needsPush);
      if (needsPush) void this.pushToDrive(merged);
      return merged;
    } catch (err) {
      console.warn('[CardIndexService] Drive unavailable — using local cache:', err);
      return cached ?? emptyIndex();
    }
  }

  /**
   * Persist the index. The local cache is written first and unconditionally, so
   * a card survives a failed upload; it's marked dirty and retried on the next
   * getIndex.
   */
  async updateIndex(index: CardsIndexFile): Promise<void> {
    index.synced_at = Date.now();
    writeCache(index, true);

    if (authSession.state.status !== 'active') return;
    await this.pushToDrive(index);
  }

  /** Best-effort upload. Clears the dirty flag only when Drive confirms. */
  private async pushToDrive(index: CardsIndexFile): Promise<void> {
    try {
      await this.initialize();
      if (!this.indexFileId) return;
      await driveService.updateJSON(this.indexFileId, index);
      writeCache(index, false);
    } catch (err) {
      console.warn('[CardIndexService] Could not push index (queued for next sync):', err);
    }
  }
}

let instance: CardIndexService | null = null;
export function getCardIndexService(): CardIndexService {
  if (!instance) instance = new CardIndexService();
  return instance;
}
